import React, { FC, HTMLProps, useEffect, useMemo } from 'react'
import Link from 'next/link'
import s from './MediaCardCompilation.module.sass'

import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { getPlaceholderImage } from '@shared/lib/helpers/getPlaceholderImage'
import { Rating } from '@shared/ui/Feedback/Rating'
import { Title } from '@shared/ui/Typography/Title'
import { useCurrentSize } from '@shared/lib/helpers/classes'
import { sendToWorker, handleWorkerMessage } from '@shared/lib/partytown/workerUtils'
import type { WorkerMessage } from '@shared/lib/partytown/workerUtils'

import IconLike from '@icons/icon-like.svg'
import IconSelectionChevronBig from '@icons/icon-selection-chevron--big.svg'
import IconSelectionChevronMedium from '@icons/icon-selection-chevron--medium.svg'
import { ChevronWithTooltip } from '@shared/ui/Feedback/ChevronWithTooltip'
import { TypeMappingNewsOutput } from '@shared/api/middleware/mappingAPI/news/type'
import { TypeMappingSelectionOutput } from '@shared/api/middleware/mappingAPI/selection/type'
import { PickAnalytic } from '@shared/api/analytics/types/types'
import { IconButton } from '@shared/ui/Actions/IconButton'
import { Favorite } from '@features/Favorite'
import { MediaImg } from '@shared/ui/Media/MediaImg'
import { ImageModel } from '@shared/ui/Media/MediaImg/types/types'

// D-desktop - M-mobile
// type size = 'D1x1 D2x1 M1x1'
interface Props {
  id: string | number
  title: string
  sizes: string
  img: {
    horizontal: string
    vertical: string
  }
  favorite: {
    category: 'news' | 'selection'
    analytic: {
      data: TypeMappingNewsOutput | TypeMappingSelectionOutput
      other: PickAnalytic<'section_name'>
    }
  }
  promo?: boolean
  chevron?: boolean
  link: string
  className?: string
}

const sizesImage = {
  D2x1: { width: 610 * 2, height: 297 * 2 },
  D1x1: { width: 297 * 2, height: 297 * 2 },
  M1x1: { width: 261 * 2, height: 261 * 2 },
}

const MediaCardCompilation: FC<Props & HTMLProps<HTMLDivElement>> = React.forwardRef(
  ({ id, sizes, favorite, className = '', title, img, promo = false, chevron = false, link, ...props }, ref) => {
    const [size] = useCurrentSize(sizes)

    useEffect(() => {
      // Обработка сообщений от Web Worker
      handleWorkerMessage((message: WorkerMessage) => {
        if (message.type === 'IMAGE_LOADED') {
          console.log('Изображение загружено:', message.payload)
        }
      })
    }, [])

    const firstNumbers = parseInt(title)
    const firstNumbersIsTrue = !!firstNumbers && firstNumbers > 2
    // @ts-ignore
    const titleFormatted = firstNumbersIsTrue ? title.replace(firstNumbers, '') : title

    const handleImageLoad = () => {
      sendToWorker({
        type: 'IMAGE_LOADED',
        payload: {
          id,
          size,
          timestamp: Date.now(),
        },
      })
    }

    const getPhotoLink = useMemo((): ImageModel => {
      const result = {
        Url: null,
        Width: null,
        Height: null,
        Crop: null,
        Description: null,
        Copyright: null,
        Author: null,
      }

      result.Url = size === 'D2x1' ? img.horizontal : img.vertical

      result.Width = sizesImage[size].width
      result.Height = sizesImage[size].height

      return result
    }, [img])

    return (
      <ErrorBoundary>
        <div data-id={id} className={`${s.card} ${className}`} data-size={size} title={title} {...props} ref={ref}>
          <Link href={link} scroll={false}>
            {promo && <Rating id={s.promo} className={s.promo} mode={'fill'} isPromo sizes={'S'} />}

            {chevron && size === 'M1x1' && (
              <ChevronWithTooltip icon={IconSelectionChevronMedium} className={s['afisha-selection-chevron--medium']} />
            )}
            {chevron && size !== 'M1x1' && (
              <ChevronWithTooltip icon={IconSelectionChevronBig} className={s['afisha-selection-chevron--big']} />
            )}

            {!!firstNumbersIsTrue && <span className={s.numbers}>{firstNumbers}</span>}

            <Title tag={'h5'} sizes={'h4'} className={s.title}>
              {titleFormatted}
            </Title>

            <Favorite
              category={favorite.category}
              id={Number(id)}
              analytic={{ data: favorite.analytic.data, other: favorite.analytic.other }}
              className={s.favorite}>
              <IconButton sizes={'S'} mode={'white'} icon={IconLike} />
            </Favorite>

            <MediaImg
              image={getPhotoLink}
              width={sizesImage[size].width}
              height={sizesImage[size].height}
              className={s['image-wrapper']}
              onLoad={handleImageLoad}
              flexible={true}
              dimmed={'gradient'}
              borderRadius={'24px'}
            />
          </Link>
        </div>
      </ErrorBoundary>
    )
  },
)

MediaCardCompilation.displayName = 'MediaCardCompilation'

export default MediaCardCompilation
